import {
  IDeedRun,
  IDeedTemplate,
  DeedUnavailableReason,
} from '../hooks/reactQuerys/DeedGenerationQueries';

/**
 * Urkundenerzeugung im Vorgang — die Entscheidungen, die keine Oberfläche sind (#1245).
 *
 * Hier steht, was der Benutzer zu sehen bekommt: bei keinem Baum ein Satz statt
 * eines Fehlers, bei genau einem keine Auswahl, bei mehreren eine Auswahl mit
 * der Vorgabe vorn. Und welche Variante überhaupt zur Sprache kommt — nur
 * importierte Bäume kennen mGB/oGB, ein nativer Baum nicht.
 *
 * Reine Funktionen, weil genau das die Aussagen sind, die stimmen müssen.
 */

/** Wie der Vorgang die Auswahl anbietet. */
export type OfferMode = 'none' | 'single' | 'choice';

export const offerMode = (templates: IDeedTemplate[]): OfferMode => {
  const usable = templates.filter(entry => entry.available);
  if (usable.length === 0) return 'none';
  if (usable.length === 1) return 'single';
  return 'choice';
};

/**
 * Was vorausgewählt ist.
 *
 * Die Vorgabe der Rechtsgeschäftsart, sonst der einzige Baum, sonst nichts.
 * Bewusst wird die letzte Wahl des Benutzers NICHT gemerkt: eine Vorgabe zu
 * setzen ist eine Verwaltungsentscheidung, kein Nebeneffekt des Arbeitens.
 */
export const preselectedTemplate = (
  templates: IDeedTemplate[],
  defaultTemplateId: number | null
): number | null => {
  const usable = templates.filter(entry => entry.available);
  const fromDefault = usable.find(
    entry => entry.idDecisionTemplate === defaultTemplateId
  );
  if (fromDefault) return fromDefault.idDecisionTemplate;
  if (usable.length === 1) return usable[0].idDecisionTemplate;
  return null;
};

/** In der Reihenfolge, in der sie angeboten werden: Vorgabe zuerst, dann wie freigegeben. */
export const offeredTemplates = (templates: IDeedTemplate[]): IDeedTemplate[] =>
  templates
    .filter(entry => entry.available)
    .slice()
    .sort((a, b) => {
      if (a.isDefault !== b.isDefault) return a.isDefault ? -1 : 1;
      if (a.sortOrder !== b.sortOrder) return a.sortOrder - b.sortOrder;
      return a.name.localeCompare(b.name, 'de');
    });

/**
 * Muss der Benutzer eine Variante wählen?
 *
 * Nur bei einem importierten Baum mit mehr als einer verwendbaren Variante. Es
 * gibt keine Regel, die mGB oder oGB aus dem Vorgang ableiten könnte — deshalb
 * wird gefragt statt geraten. Native Bäume kennen das Konzept nicht.
 */
export const needsVariantChoice = (template: IDeedTemplate | null): boolean =>
  Boolean(template && !template.isNative && template.variants.length > 1);

/** Die Variante, mit der gestartet wird, wenn nicht gefragt wird. */
export const initialVariant = (
  template: IDeedTemplate | null
): string | null => {
  if (!template || template.isNative) return null;
  return template.variants[0] ?? null;
};

/** Was im Vorgang steht, wenn nichts freigegeben ist. */
export const NO_TEMPLATE_TEXT =
  'Für diese Rechtsgeschäftsart ist noch kein Entscheidungsbaum für die Urkundenerstellung freigegeben.';

export const NO_TRANSACTION_TYPE_TEXT =
  'Dieser Vorgang hat noch keine Rechtsgeschäftsart. Ohne sie lässt sich kein Entscheidungsbaum zuordnen.';

/** Klartext für die Verwaltungsansicht, warum ein Baum nicht angeboten wird. */
const REASON_TEXT: Record<DeedUnavailableReason, string> = {
  NO_PUBLISHED_VERSION: 'noch keine veröffentlichte Version',
  NOT_ACTIVE: 'nicht aktiv',
  VALIDATION_FAILED: 'Prüfung fehlgeschlagen',
  ASSIGNMENT_DISABLED: 'Freigabe ausgesetzt',
};

export const unavailableText = (
  reason: DeedUnavailableReason | null
): string => (reason ? REASON_TEXT[reason] : 'nicht verfügbar');

/**
 * Wie ein Baum im Vorgang heißt.
 *
 * Der Name, sonst nichts. Herkunft, Registernummern und Legacy-Schlüssel
 * interessieren hier niemanden (§6); die Gruppe steht daneben, weil sie im
 * Notariat die fachliche Einordnung ist.
 */
export const templateLabel = (template: IDeedTemplate): string =>
  template.group ? `${template.group} · ${template.name}` : template.name;

/** Kurzfassung des Standes eines Laufs. */
export const runStatusText = (run: IDeedRun): string => {
  switch (run.status) {
    case 'OPEN':
      return 'wartet auf Word';
    case 'CLAIMED':
      return 'in Word geöffnet';
    case 'INSERTED':
      return `eingefügt (${run.sectionsInserted} Abschnitte)`;
    case 'CANCELLED':
      return 'verworfen';
    default:
      return run.status;
  }
};

/** „Kaufvertrag Standard, Version 2" — womit diese Urkunde erzeugt wurde. */
export const runProvenance = (run: IDeedRun): string =>
  `${run.templateName}, Version ${run.versionNumber}`;
